import api from "./api";
import { getMe, primaryRoleOf, resetCaches, roleKeysOf } from "./core";

async function completeTokenLogin(data) {
  localStorage.setItem("access_token", data.access_token);
  // [NEW] the new session's end time is stored first, so the expired-session guard (services/api.js) lets it through
  localStorage.setItem("pms_session_expires_at", String(Date.now() + (data.expires_in || 3600) * 1000));
  const me = await getMe(true);
  const roleKey = primaryRoleOf(me);
  const user = {
    id: me.employee_id,
    employeeId: me.employee_id,
    username: me.username,
    name: me.employee_name,
    roleKey,
    roleKeys: roleKeysOf(me),
    mustChangePassword: Boolean(data.must_change_password || me.must_change_password),
    expiresAt: Date.now() + (data.expires_in || 3600) * 1000,
  };
  localStorage.setItem("pms_session_expires_at", String(user.expiresAt));
  return { data: { access_token: data.access_token, user } };
}

// [NEW] PM-12: Microsoft (Entra ID) sign-in. MSAL gets an access token for the PMS API; the backend
// (POST /auth/entra/exchange) checks it and returns the same session as a password login.
export async function loginWithMicrosoft() {
  resetCaches();
  const { getMicrosoftAccessToken } = await import("./entraAuth");
  const accessToken = await getMicrosoftAccessToken();
  const { data } = await api.post("/auth/entra/exchange", { access_token: accessToken });
  return completeTokenLogin(data);
}

export async function loginUser({ username, password }) {
  resetCaches();
  const { data } = await api.post("/auth/login", { username: String(username || "").trim(), password });
  // ===== OTP DISABLED (temporary) - START =====
  // if (data?.mfa_required) return { data };
  // ===== OTP DISABLED (temporary) - END =====
  if (data?.mfa_required) {
    throw new Error("Email OTP is switched off in this app for now, but the server still asks for it. Set LOGIN_OTP_ENABLED=false in the backend .env and restart the backend.");
  }
  return completeTokenLogin(data);
}

// ===== OTP DISABLED (temporary) - START =====
// export async function verifyLoginOtp({ challengeToken, otp }) {
//   const { data } = await api.post("/auth/verify-otp", {
//     challenge_token: challengeToken,
//     otp: String(otp || "").trim(),
//   });
//   return completeTokenLogin(data);
// }
//
// export const resendLoginOtp = ({ challengeToken }) =>
//   api.post("/auth/resend-otp", { challenge_token: challengeToken });
// ===== OTP DISABLED (temporary) - END =====

export const getCurrentUser = () => getMe(true).then((data) => ({ data }));

export async function logoutUser(token = localStorage.getItem("access_token")) {
  try {
    if (token) await api.post("/auth/logout", null, { headers: { Authorization: `Bearer ${token}` } });
  } catch {
  } finally {
    resetCaches();
  }
  return { data: { success: true } };
}

export const pingSession = () => api.get("/auth/me", { skipAuthRedirect: true });

export const changePassword = ({ currentPassword, newPassword }) =>
  api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });

export const getPublicLoginOptions = async () => ({ data: { group: null, password_login_enabled: true, providers: [] } });

export const requestPasswordReset = ({ username }) =>
  api.post("/auth/forgot-password", { username: String(username || "").trim() });

// The token sent here can be the e-mail link token or (preferred) the short-lived exchange token
// returned by exchangeResetLink(). The backend accepts both.
export const resetPassword = ({ token, newPassword }) =>
  api.post("/auth/reset-password", { token, new_password: newPassword });

// [NEW] GET /auth/reset-password/validate?token=... - read-only check when the e-mail link is opened.
export const validateResetLink = (token) =>
  api.get("/auth/reset-password/validate", { params: { token } });

// [NEW] POST /auth/reset-password/exchange - swaps the e-mail token for a one-time exchange token
// (valid LINK_EXCHANGE_TTL_MINUTES, default 10). The e-mail token stops working after this call,
// so the page removes it from the address bar and uses only the exchange token.
export const exchangeResetLink = (token) =>
  api.post("/auth/reset-password/exchange", { token });

export const isResetEndpointMissing = (error) => [404, 405, 501].includes(error?.response?.status);

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;
export function passwordPolicy(password) {
  return {
    length: password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    digit: /[0-9]/.test(password),
    special: /[^A-Za-z0-9\s]/.test(password),
    noSpaces: !/\s/.test(password),
  };
}
